import React, { useState } from 'react'
import { Icon } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Link, useHistory } from 'react-router-dom'
import clsx from 'clsx'
import { useSectionNav } from 'app/hooks/useSectionNav'
import useSettings from 'app/hooks/useSettings'

const useStyles = makeStyles(({ palette }) => ({
  sheet: {
    position: 'fixed',
    inset: 0,
    zIndex: 120,
    background: palette.primary.contrastText,
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    flexShrink: 0,
    height: 64,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 18px',
    borderBottom: `1px solid ${palette.divider}`,
  },
  title: {
    margin: 0,
    fontSize: 19,
    fontWeight: 700,
    color: palette.text.primary,
  },
  close: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: palette.action.hover,
    color: palette.text.secondary,
  },
  list: {
    flex: 1,
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    height: 68,
    padding: '0 18px',
    borderBottom: `1px solid ${palette.divider}`,
    color: 'inherit',
    textDecoration: 'none',
  },
  rowButton: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
  },
  rowActive: {
    background: palette.action.selected,
  },
  iconChip: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: palette.action.hover,
    color: palette.text.primary,
  },
  iconChipActive: {
    color: palette.primary.main,
  },
  label: {
    flex: 1,
    fontSize: 15,
    color: palette.text.primary,
  },
  labelActive: {
    fontWeight: 600,
    color: palette.primary.main,
  },
  chevron: {
    opacity: 0.35,
    transition: 'transform 150ms ease',
  },
  chevronOpen: {
    opacity: 0.7,
    transform: 'rotate(180deg)',
  },
  submenu: {
    background: palette.background.default,
  },
  subRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 52,
    padding: '0 18px 0 72px',
    borderBottom: `1px solid ${palette.divider}`,
    color: 'inherit',
    textDecoration: 'none',
  },
  subRowActive: {
    background: palette.action.selected,
  },
  subLabel: {
    fontSize: 14,
    color: palette.text.secondary,
  },
}))

// The mobile-only replacement for `SectionRail`: instead of popping the
// 80px desktop icon rail open over the edge of the phone screen (no
// backdrop, no labels — the bug this component fixes), "Menu" opens a
// full-screen sheet listing every visible section as a full-width,
// generously-tall row. Only ever mounted on mobile — see
// `Layout1Sidenav`.
//
// A section with more than one page doesn't jump straight to its
// first page: tapping the row expands a dropdown of that section's
// pages (submenus) right here, instead of relying on a separate tab
// row in the top-level nav (see `Layout1Topbar`, which drops that tab
// row on mobile). A section with a single page still navigates
// straight there, same as before.
const MobileSectionSheet = () => {
  const classes = useStyles()
  const history = useHistory()
  const { updateSettings } = useSettings()
  const { sections, activeSection, activeChild } = useSectionNav()
  const [expandedId, setExpandedId] = useState(() => activeSection?.id ?? null)

  const close = () =>
    updateSettings({ layoutSettings: { leftSidebar: { mode: 'close' } } })

  const goTo = (path) => (event) => {
    event.preventDefault()
    close()
    history.push(path)
  }

  const toggle = (id) =>
    setExpandedId((current) => (current === id ? null : id))

  return (
    <div className={classes.sheet}>
      <div className={classes.header}>
        <h1 className={classes.title}>Seções</h1>
        <button
          type="button"
          className={classes.close}
          onClick={close}
          aria-label="Fechar"
        >
          <Icon fontSize="small">close</Icon>
        </button>
      </div>

      <div className={classes.list}>
        {sections.map((section) => {
          // External links (e.g. the Django admin) stay in the submenu,
          // opening in a new browser tab; only an internal page can be
          // the row's direct target.
          const pages = section.children
          if (pages.length === 0) return null

          const isActive = activeSection?.id === section.id

          if (pages.length === 1 && pages[0].type !== 'extLink') {
            const target = pages[0]
            return (
              <Link
                key={section.id}
                to={target.path}
                onClick={goTo(target.path)}
                className={clsx(classes.row, {
                  [classes.rowActive]: isActive,
                })}
              >
                <div
                  className={clsx(classes.iconChip, {
                    [classes.iconChipActive]: isActive,
                  })}
                >
                  <Icon>{section.icon}</Icon>
                </div>
                <span
                  className={clsx(classes.label, {
                    [classes.labelActive]: isActive,
                  })}
                >
                  {section.name}
                </span>
                <Icon style={{ opacity: isActive ? 1 : 0.35 }}>
                  {isActive ? 'check' : 'chevron_right'}
                </Icon>
              </Link>
            )
          }

          const isExpanded = expandedId === section.id

          return (
            <div key={section.id}>
              <button
                type="button"
                aria-expanded={isExpanded}
                onClick={() => toggle(section.id)}
                className={clsx(classes.row, classes.rowButton, {
                  [classes.rowActive]: isActive,
                })}
              >
                <div
                  className={clsx(classes.iconChip, {
                    [classes.iconChipActive]: isActive,
                  })}
                >
                  <Icon>{section.icon}</Icon>
                </div>
                <span
                  className={clsx(classes.label, {
                    [classes.labelActive]: isActive,
                  })}
                >
                  {section.name}
                </span>
                <Icon
                  className={clsx(classes.chevron, {
                    [classes.chevronOpen]: isExpanded,
                  })}
                >
                  expand_more
                </Icon>
              </button>

              {isExpanded && (
                <div className={classes.submenu}>
                  {pages.map((page) => {
                    if (page.type === 'extLink') {
                      return (
                        <a
                          key={page.path}
                          href={page.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={close}
                          className={classes.subRow}
                        >
                          <span className={classes.subLabel}>{page.name}</span>
                          <Icon fontSize="small" style={{ opacity: 0.5 }}>
                            open_in_new
                          </Icon>
                        </a>
                      )
                    }

                    const pageActive =
                      isActive && activeChild?.path === page.path
                    return (
                      <Link
                        key={page.path}
                        to={page.path}
                        onClick={goTo(page.path)}
                        className={clsx(classes.subRow, {
                          [classes.subRowActive]: pageActive,
                        })}
                      >
                        <span
                          className={clsx(classes.subLabel, {
                            [classes.labelActive]: pageActive,
                          })}
                        >
                          {page.name}
                        </span>
                        {pageActive && (
                          <Icon fontSize="small" style={{ opacity: 1 }}>
                            check
                          </Icon>
                        )}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default MobileSectionSheet
